import React, { useState } from 'react';
import { FileText, Clock, ExternalLink, User, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ParteDetalleDialog } from './ParteDetalleDialog';
import { JornadaDetalleDialog } from './JornadaDetalleDialog';

interface ParteData {
  id: string;
  numero_parte: string | null;
  fecha_trabajo: string;
  hora_inicio?: string;
  hora_fin?: string | null;
  descripcion_trabajo: string;
  duracion_total: string | null;
  estado: string;
  tipo_intervencion?: string;
  resultado_intervencion?: string | null;
  motivo_no_resuelto?: string | null;
  materiales_necesarios?: string | null;
  firma_cliente: string | null;
  firma_tecnico?: string | null;
  tecnico?: { nombre: string } | null;
  cliente?: { nombre: string } | null;
  cliente_libre?: string | null;
  orden_trabajo?: { numero_ot: string; titulo: string } | null;
}

interface JornadaData {
  id: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string | null;
  horas_trabajadas: number | null;
  tipo_trabajo: string;
  descripcion_trabajo: string | null;
  es_hora_extra?: boolean;
  tecnico?: { nombre: string } | null;
  cliente?: { nombre: string } | null;
  orden_trabajo?: { numero_ot: string; titulo: string } | null;
  obra?: { nombre: string } | null;
}

interface DocumentacionPopoverProps {
  children: React.ReactNode;
  partes: ParteData[];
  jornadas: JornadaData[];
  ordenId: string;
}

const tipoTrabajoLabels: Record<string, string> = {
  instalacion: 'Instalación',
  puesta_en_marcha: 'Puesta en marcha',
  auditoria: 'Auditoría',
  visita_obra: 'Visita obra',
  correctivos: 'Correctivos',
  mantenimiento: 'Mantenimiento',
};

export const DocumentacionPopover: React.FC<DocumentacionPopoverProps> = ({
  children,
  partes,
  jornadas,
  ordenId,
}) => {
  const [selectedParte, setSelectedParte] = useState<ParteData | null>(null);
  const [selectedJornada, setSelectedJornada] = useState<JornadaData | null>(null);
  const [parteDialogOpen, setParteDialogOpen] = useState(false);
  const [jornadaDialogOpen, setJornadaDialogOpen] = useState(false);

  const hasContent = partes.length > 0 || jornadas.length > 0;

  if (!hasContent) {
    return <>{children}</>;
  }

  const handleParteClick = (parte: ParteData) => {
    setSelectedParte(parte);
    setParteDialogOpen(true);
  };

  const handleJornadaClick = (jornada: JornadaData) => {
    setSelectedJornada(jornada);
    setJornadaDialogOpen(true);
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <button className="cursor-pointer hover:opacity-80 transition-opacity">
            {children}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-3 border-b">
            <h4 className="font-semibold text-sm">Documentación de la OT</h4>
            <p className="text-xs text-muted-foreground mt-1">
              {partes.length} parte{partes.length !== 1 ? 's' : ''} · {jornadas.length} jornada{jornadas.length !== 1 ? 's' : ''}
            </p>
          </div>

          {partes.length > 0 && (
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium">Partes de Trabajo</span>
              </div>
              <div className="space-y-2">
                {partes.map((parte) => (
                  <div
                    key={parte.id}
                    className="p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        #{parte.numero_parte || 'Sin número'}
                      </span>
                      <Badge
                        variant={parte.estado === 'finalizado' ? 'default' : 'secondary'}
                        className="text-[10px] h-5"
                      >
                        {parte.estado === 'finalizado' ? 'Firmado' : parte.estado}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(parte.fecha_trabajo), 'dd/MM/yyyy', { locale: es })}
                      {parte.duracion_total && (
                        <>
                          <span>·</span>
                          <Clock className="h-3 w-3" />
                          {parte.duracion_total}
                        </>
                      )}
                    </div>
                    {parte.tecnico?.nombre && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <User className="h-3 w-3" />
                        {parte.tecnico.nombre}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                      {parte.descripcion_trabajo}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2 h-7 text-xs"
                      onClick={() => handleParteClick(parte)}
                    >
                      Ver detalle
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {partes.length > 0 && jornadas.length > 0 && <Separator />}

          {jornadas.length > 0 && (
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium">Jornadas</span>
              </div>
              <div className="space-y-2">
                {jornadas.map((jornada) => (
                  <div
                    key={jornada.id}
                    className="p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {format(new Date(jornada.fecha), 'dd/MM/yyyy', { locale: es })}
                      </span>
                      <Badge variant="outline" className="text-[10px] h-5">
                        {jornada.horas_trabajadas?.toFixed(1) || '0'}h
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <Clock className="h-3 w-3" />
                      {jornada.hora_inicio}
                      {jornada.hora_fin ? ` - ${jornada.hora_fin}` : ' (en curso)'}
                    </div>
                    {jornada.tecnico?.nombre && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <User className="h-3 w-3" />
                        {jornada.tecnico.nombre}
                      </div>
                    )}
                    {jornada.tipo_trabajo && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] h-5 mt-1"
                      >
                        {tipoTrabajoLabels[jornada.tipo_trabajo] || jornada.tipo_trabajo}
                      </Badge>
                    )}
                    {jornada.descripcion_trabajo && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                        {jornada.descripcion_trabajo}
                      </p>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2 h-7 text-xs"
                      onClick={() => handleJornadaClick(jornada)}
                    >
                      Ver detalle
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Diálogos de detalle */}
      <ParteDetalleDialog
        open={parteDialogOpen}
        onOpenChange={setParteDialogOpen}
        parte={selectedParte}
      />
      <JornadaDetalleDialog
        open={jornadaDialogOpen}
        onOpenChange={setJornadaDialogOpen}
        jornada={selectedJornada}
      />
    </>
  );
};
